import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { differenceInDays, format } from 'date-fns';
import { Placeholder } from '../../commons/components';
import rowsForMonthlyData from '../constants/rowsForMonthlyData';
import { getDateFromString } from '../../../utils/helpers';

const TableRow = ({
  title,
  data = {},
  field = '',
  divisor,
  showPercentage = false,
  clickable = false,
  onClick = () => {},
  getPercentageOrAvg = () => {},
}) => {
  const navigateToDetails = () => {
    if (!clickable) {
      return;
    }
    onClick(field);
  };

  let value;
  if (divisor && !isNaN(value)) {
    value = (value / divisor).toFixed(2);
  }
  if (showPercentage && getPercentageOrAvg) {
    value = getPercentageOrAvg();
  } else {
    value = Number(data?.data?.[field]);
    if (divisor && !isNaN(value)) {
      value = (value / divisor).toFixed(2);
    } 
  }

  return (
    <tr>
      <th
        scope='row'
        className={`first-column w-25${clickable ? ' link-style' : ''}`}
        onClick={navigateToDetails}
      >
        {title}
      </th>
      <td style={{ textAlign: 'center' }}>
        {data.loading ? (
          <Placeholder width='90%' height={20} />
        ) : (
          isNaN(value) ? '-' : value?.toLocaleString('en-US')
        )}
      </td>
    </tr>
  );
};

const Last30DaysContainer = ({ companySelected, startValue, endValue, selectedFilter }) => {
  const last30DaysRecords = useSelector((state) => state.kpi.last30Days);
  const navigate = useNavigate();

  const getHeaderSubtitle = () => {
    if (startValue && endValue) {
      const sDate = getDateFromString(startValue);
      const eDate = getDateFromString(endValue);
      return `${format(sDate, 'do MMM')} - ${format(eDate, 'do MMM yyyy')}`;
    }
  };

  const getDataForRows = (field) => {
    switch (field) {
      case 'registered_users':
        return last30DaysRecords?.paidRegisteredUsers;
      case 'activeUsers':
        return last30DaysRecords?.activeUsers;
      case 'total_users_played_content':
        return last30DaysRecords?.totalUsersPlayedContent;
      case 'total_mins':
        return last30DaysRecords?.totalMinsPlayed;
      case 'total_users_rated_content':
        return last30DaysRecords?.usersRatedTheContent;
      case 'total_users_favorite_the_content':
        return last30DaysRecords?.usersFavoriteTheContent;
      case 'total_steps':
        return last30DaysRecords?.totalStepsCaptured;
      case 'total_users_syncing_steps':
        return last30DaysRecords?.totalUsersSyncingSteps;
      case 'personal_coaching_bookings_completed':
        return last30DaysRecords?.numberOfPcCompleted;
      case 'personal_coaching_bookings':
        return last30DaysRecords?.numberOfPcBookings;
      case 'total_users_taking_waterintake':
        return last30DaysRecords?.numberOfUsersTakingWaterIntake;
      case 'total_water_intake':
        return last30DaysRecords?.numberOfUsersTakingWaterIntake;
      case 'total_reward_points_earned':
        return last30DaysRecords?.totalEarnedRewardPoints;
      case 'total_reward_points_redeemed':
        return last30DaysRecords?.totalRedeemedRewardPoints;
      case 'total_users_earned_rewards':
        return last30DaysRecords?.totalUsersEarnedReward;
      case 'total_users_redeemed_rewards':
        return last30DaysRecords?.totalUsersRedeemedReward;
      case 'total_push_sent':
        return last30DaysRecords?.totalPushSent;
      case 'total_challenges_launched':
        return last30DaysRecords?.totalChallengesLaunched;
      case 'total_challenges_joined':
        return last30DaysRecords?.totalUsersJoinedInChallenge;
      case 'total_challenges_participated':
        return last30DaysRecords?.totalUsersParticipatedInChallenge;
      case 'programs_joined':
        return last30DaysRecords?.programJoined;
      case 'programs_completed':
        return last30DaysRecords?.programCompleted;
      case 'audio_books_redeemed':
        return last30DaysRecords?.bookRead;
      case 'total_meal_images_uploaded':
        return last30DaysRecords?.totalMealImagesUploaded;
      case 'total_meals_added':
        return last30DaysRecords?.totalMealsAdded;
      case 'total_ai_messages':
        return last30DaysRecords?.totalAICoachMessages;
      case 'unique_nutrition_users':
        return last30DaysRecords?.uniqueNutritionUsers;
      default:
        return {};
    }
  };

  function getAvgUsePerDay() {
    const avgMinsPlayedPerDay = getAvgMinutesPlayedPerDay();
    const stepsToMins = getStepsToMinsConversion();
    const avgWaterIntakePerUserPerDay = getAvgWaterIntakePerUserPerDay();

    let sum = 0;

    if (!isNaN(avgMinsPlayedPerDay)) {
      sum += Number(avgMinsPlayedPerDay);
    }
    if (!isNaN(stepsToMins)) {
      sum += Number(stepsToMins);
    }
    if (!isNaN(avgWaterIntakePerUserPerDay)) {
      sum += Number(avgWaterIntakePerUserPerDay);
    }
    return sum.toFixed(2);
  }

  function getActiveUsersPercentageForMonth() {
    const totalUsers = last30DaysRecords?.paidRegisteredUsers?.data?.registered_users;
    const realUsers = last30DaysRecords?.activeUsers?.data?.activeUsers;
    if (isNaN(totalUsers) || isNaN(realUsers)) {
      return '-';
    }
    if (totalUsers && realUsers) {
      return ((realUsers * 100) / totalUsers).toFixed(2);
    }
  }

  function getAvgMinutesPlayedPerDay() {
    let totalDays = differenceInDays(new Date(endValue), new Date(startValue)) + 1;
    let totalMins = Number(last30DaysRecords?.totalMinsPlayed?.data?.total_mins);
    let realActiveUsers = Number(last30DaysRecords?.activeUsers?.data?.activeUsers);

    if (isNaN(totalDays) || isNaN(totalMins) || isNaN(realActiveUsers)) {
      return '-';
    }
    const result = totalMins / (realActiveUsers * totalDays);
    return result?.toFixed(2) || 0;
  }

  function getUsersPercentageRatedTheContent() {
    const totalUsersRated = last30DaysRecords?.usersRatedTheContent?.data?.total_users_rated_content;
    const totalUsersPlayed = last30DaysRecords?.totalUsersPlayedContent?.data?.total_users_played_content;

    if (totalUsersRated && totalUsersPlayed) {
      return ((totalUsersRated * 100) / totalUsersPlayed).toFixed(2);
    }
    return '-';
  }

  function getAvgStepsPerUserPerDay() {
    let totalDays = differenceInDays(new Date(endValue), new Date(startValue)) + 1;
    let totalSteps = last30DaysRecords?.totalStepsCaptured?.data?.total_steps;
    let totalUsers = last30DaysRecords?.totalUsersSyncingSteps?.data?.total_users_syncing_steps;

    if (isNaN(totalDays) || isNaN(totalSteps) || isNaN(totalUsers)) {
      return '-';
    }
    const result = totalSteps / (totalUsers * totalDays);
    return result.toFixed(2);
  }

  function getPercentOfActiveUsersWhoSyncSteps() {
    const tmpTotalUsersSyncingSteps = last30DaysRecords?.totalUsersSyncingSteps?.data?.total_users_syncing_steps;
    const realActiveUsers = last30DaysRecords?.activeUsers?.data?.activeUsers;

    if (isNaN(tmpTotalUsersSyncingSteps) || isNaN(realActiveUsers)) {
      return '-';
    }
    const result = (tmpTotalUsersSyncingSteps * 100) / realActiveUsers || 0;
    return result.toFixed(2);
  }

  function getStepsToMinsConversion() {
    const getAvgStepsPerUserPerDayResult = getAvgStepsPerUserPerDay();
    if (isNaN(getAvgStepsPerUserPerDayResult)) {
      return '-';
    }
    return (getAvgStepsPerUserPerDayResult / 75).toFixed(2)
  }

  function getAvgWaterIntakePerUserPerDay() {
    let tempUserCount =
        last30DaysRecords?.numberOfUsersTakingWaterIntake?.data?.total_users_taking_waterintake;
    let tempTotalWaterIntake = last30DaysRecords?.numberOfUsersTakingWaterIntake?.data?.total_water_intake;
    let totalDays = differenceInDays(new Date(endValue), new Date(startValue)) + 1;

    if (isNaN(tempUserCount) || isNaN(tempTotalWaterIntake) || isNaN(totalDays)) {
      return '-';
    }
    const result = tempTotalWaterIntake / (tempUserCount * totalDays);
    return result.toFixed(2);
  }

  function getPercentageOfPushRead() {
    if (last30DaysRecords?.totalPushSent?.loading) {
      return <Placeholder width='90%' height={20} />;
    }
    const tempTotalPushSent = last30DaysRecords?.totalPushSent?.data?.total_push_sent;
    const tempTotalPushRead = last30DaysRecords?.totalPushSent?.data?.total_push_read;
    if (tempTotalPushSent && tempTotalPushRead) {
      return ((tempTotalPushRead * 100) / tempTotalPushSent).toFixed(2);
    }
    return 0;
  }

  const navigateToDetails = (field) => {
    return navigate(`/overview/details?field=${field}`, {
      state: {
        filterType: selectedFilter?.value ?? filterType.last30Days,
        startDate: startValue,
        endDate: endValue,
      }}
    );
  };

  // console.log(startValue, endValue);

  return (
    <table className='data-table table table-bordered'>
      <thead>
        <tr>
          <th scope='col' className='first-column header w-25'></th>
          <th scope='col' className='header' style={{ textAlign: 'center' }}>
            <div className='d-flex flex-column'>
              <span>Last 30 Days</span>
              {getHeaderSubtitle() && <span className='subtitle'>{'('}{getHeaderSubtitle()}{')'}</span>}
            </div>
          </th>
        </tr>
      </thead>
      <tbody>
        <>
          {rowsForMonthlyData.map((row, index) => (
            <TableRow
              key={row.key + index}
              clickable={row.clickable ?? companySelected[0]?._id === 'ALL'}
              onClick={navigateToDetails}
              title={row.title}
              data={getDataForRows(row.key)}
              field={row.key}
              divisor={row.key === 'total_steps' ? 1000000 : null}
              showPercentage={row.showPercentage}
              getPercentageOrAvg={
                row.showPercentage
                  ? row.key === 'averageUsagePerDayPerUser'
                    ? getAvgUsePerDay
                    : row.key === 'activeUserPercentage'
                    ? getActiveUsersPercentageForMonth
                    : row.key === 'avgMinutesPlayedPerDay'
                    ? getAvgMinutesPlayedPerDay
                    : row.key === 'userRatingPercentage'
                    ? getUsersPercentageRatedTheContent
                    : row.key === 'avgStepsPerUserPerDay'
                    ? getAvgStepsPerUserPerDay
                    : row.key === 'activeUserPercentageWhoSyncedSteps'
                    ? getPercentOfActiveUsersWhoSyncSteps
                    : row.key === 'stepToMinutesConversion'
                    ? getStepsToMinsConversion
                    : row.key === 'avgWaterIntake'
                    ? getAvgWaterIntakePerUserPerDay
                    : row.key === 'pushReadPercentage'
                    ? getPercentageOfPushRead
                    : () => {}
                  : () => {}
              }
            />
          ))}
        </>
      </tbody>
    </table>
  );
};

export default Last30DaysContainer;
